"use client";

import { useNumComponentsByClientSize } from "@faire/retailer-visitor-shared/components/SDUI/UPLT/useNumComponentsByClientSize";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { UserPerceivedLoadTimingComponentsPerClientSize } from "@faire/web--source/common/performance/userPerceivedLoadTiming//calculateNumComponentsPerClientSize";
import { useDefaultTrackResultCallback } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useDefaultTrackResultCallback";
import { UserPerceivedLoadTimingContext } from "@faire/web--source/common/performance/userPerceivedLoadTiming/UserPerceivedLoadTimingContext";
import {
  UPLTHookOptions,
  useUserPerceivedLoadTimingWithKey,
} from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { useWaitForImagesToLoad } from "@faire/web--source/common/performance/useWaitForImagesToLoad/useWaitForImagesToLoad";
import { useMergedRefs } from "@faire/web--source/ui/hooks/useMergedRefs";
import * as React from "react";

export interface SDUIUPLTWrapperProps
  extends Omit<UPLTHookOptions, "numComponents"> {
  children?: React.ReactNode;
  numComponentsPerClientSize?: UserPerceivedLoadTimingComponentsPerClientSize;
  shouldUseWaitForImagesToLoadPromise?: boolean;
  onImageLoad?: () => void;
}

const IMAGE_PERCENTAGE_REMAINING = 0.1;
const IMAGE_COUNT_REMAINING = 1;

const IMAGE_LOADING_TIMEOUT = 7_000;

export const SDUIUPLTWrapper: React.FC<SDUIUPLTWrapperProps> = ({
  children,
  numComponentsPerClientSize,
  shouldUseWaitForImagesToLoadPromise,
  onImageLoad,
  ...rest
}) => {
  const renderNonPrimaryContentResolveRef = React.useRef<() => void>(() => {});
  const [renderNonPrimaryContentPromise] = React.useState<Promise<void>>(
    () =>
      new Promise((resolve) => {
        renderNonPrimaryContentResolveRef.current = resolve;
      })
  );
  const numComponents = useNumComponentsByClientSize(
    numComponentsPerClientSize
  );
  const { userToken } = useUserStore(["userToken"]);
  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    ...rest,
    numComponents,
    trackResultCallback: useDefaultTrackResultCallback(userToken),
  });

  const handleRenderNonPrimaryContent = () => {
    renderNonPrimaryContentResolveRef.current();
  };

  const waitForImagesToLoadRef = useWaitForImagesToLoad({
    numComponents: numComponents ?? Infinity,
    onImageLoad: (numImagesPending: number, totalNumImages: number) => {
      if (
        numImagesPending < IMAGE_COUNT_REMAINING ||
        numImagesPending / totalNumImages < IMAGE_PERCENTAGE_REMAINING
      ) {
        handleRenderNonPrimaryContent();
        onImageLoad?.();
      }
    },
    onTimeout: handleRenderNonPrimaryContent,
    timeout: IMAGE_LOADING_TIMEOUT,
  });

  const ref = useMergedRefs(
    loadTimingRef,
    shouldUseWaitForImagesToLoadPromise ? waitForImagesToLoadRef : undefined
  );

  return (
    <UserPerceivedLoadTimingContext.Provider
      value={{
        refCallback: ref,
        numComponents,
        renderNonPrimaryContentPromise,
      }}
    >
      {children}
    </UserPerceivedLoadTimingContext.Provider>
  );
};
